import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Activity, Users, DollarSign, Wifi, Shield, TrendingUp,
  Calendar, Home, Settings, Bell, ChevronRight, AlertCircle,
  CheckCircle, Clock, BarChart3, Zap, Database, Cloud,
  Monitor, Smartphone, Key, MessageSquare, Star, Package,
  Clipboard, Map, Bot, Layers, Network, Check, X,
  ArrowRight, FileText, Target, Lightbulb, Wrench, Edit2, Save, Plus, Trash2
} from 'lucide-react';
import { ctoDashboardApi, type AuditCategory as ApiAuditCategory, type Phase as ApiPhase, type SystemStatus as ApiSystemStatus, type AutomationWorkflow, type GuestMetrics, type QRTouchpoint } from '../services/ctoDashboardApi';

interface MetricCard {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ElementType;
  status?: 'good' | 'warning' | 'critical';
}

interface SystemStatus {
  name: string;
  status: 'online' | 'degraded' | 'offline';
  uptime: number;
  lastCheck: Date;
}

interface LoadingState {
  audit: boolean;
  phases: boolean;
  systems: boolean;
  automations: boolean;
  metrics: boolean;
  qr: boolean;
}

interface EditingItem {
  categoryId: number;
  itemId?: number;
  name: string;
  status: 'exists' | 'missing' | 'outdated';
  priority: 'high' | 'medium' | 'low';
  cost?: string;
  cost_type?: 'monthly' | 'one-time' | 'percentage';
  notes?: string;
}

interface Phase {
  id: number;
  name: string;
  goal: string;
  techFocus: string;
  timeframe: string;
  status: 'not-started' | 'in-progress' | 'completed';
  completedTasks: number;
  totalTasks: number;
}

const CTODashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Derive active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname.split('/').pop();
    if (!path || path === 'cto') return 'overview';
    return path;
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTab());
  const [selectedTimeRange, setSelectedTimeRange] = useState('today');
  const [notifications, setNotifications] = useState<any[]>([]);
  const [systemStatuses, setSystemStatuses] = useState<ApiSystemStatus[]>([]);
  const [auditCategories, setAuditCategories] = useState<ApiAuditCategory[]>([]);
  const [phases, setPhases] = useState<ApiPhase[]>([]);
  const [automations, setAutomations] = useState<AutomationWorkflow[]>([]);
  const [guestMetrics, setGuestMetrics] = useState<GuestMetrics[]>([]);
  const [qrTouchpoints, setQRTouchpoints] = useState<QRTouchpoint[]>([]);
  const [loading, setLoading] = useState<LoadingState>({
    audit: true,
    phases: true,
    systems: true,
    automations: true,
    metrics: true,
    qr: true
  });
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null);
  const [showAddItem, setShowAddItem] = useState<number | null>(null);
  
  // Update active tab when location changes
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location]);

  // Load data from API
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    // Load audit categories
    try {
      const categories = await ctoDashboardApi.getAuditCategories();
      setAuditCategories(categories);
      setLoading(prev => ({ ...prev, audit: false }));
    } catch (error) {
      console.error('Failed to load audit categories:', error);
      setLoading(prev => ({ ...prev, audit: false }));
    }

    // Load phases
    try {
      const phasesData = await ctoDashboardApi.getPhases();
      setPhases(phasesData);
      setLoading(prev => ({ ...prev, phases: false }));
    } catch (error) {
      console.error('Failed to load phases:', error);
      setLoading(prev => ({ ...prev, phases: false }));
    }

    // Load system statuses
    try {
      const systems = await ctoDashboardApi.getSystemStatuses();
      setSystemStatuses(systems);
      setLoading(prev => ({ ...prev, systems: false }));
    } catch (error) {
      console.error('Failed to load system statuses:', error);
      setLoading(prev => ({ ...prev, systems: false }));
    }

    // Load automations
    try {
      const automationData = await ctoDashboardApi.getAutomations();
      setAutomations(automationData);
      setLoading(prev => ({ ...prev, automations: false }));
    } catch (error) {
      console.error('Failed to load automations:', error);
      setLoading(prev => ({ ...prev, automations: false }));
    }

    // Load guest metrics
    try {
      const metrics = await ctoDashboardApi.getGuestMetrics();
      setGuestMetrics(metrics);
      setLoading(prev => ({ ...prev, metrics: false }));
    } catch (error) {
      console.error('Failed to load guest metrics:', error);
      setLoading(prev => ({ ...prev, metrics: false }));
    }

    // Load QR touchpoints
    try {
      const touchpoints = await ctoDashboardApi.getQRTouchpoints();
      setQRTouchpoints(touchpoints);
      setLoading(prev => ({ ...prev, qr: false }));
    } catch (error) {
      console.error('Failed to load QR touchpoints:', error);
      setLoading(prev => ({ ...prev, qr: false }));
    }

    // Notifications (still mocked for now)
    const mockNotifications = [
      { id: 1, type: 'warning', message: 'Room 204 AC unit requires maintenance', time: '10 min ago' },
      { id: 2, type: 'success', message: '15 new direct bookings today', time: '1 hour ago' },
      { id: 3, type: 'info', message: 'Weekly backup completed successfully', time: '3 hours ago' },
    ];
    setNotifications(mockNotifications);
  };

  // Handler functions for CRUD operations
  const handleSaveAuditItem = async () => {
    if (!editingItem) return;
    
    try {
      if (editingItem.itemId) {
        // Update existing item
        await ctoDashboardApi.updateAuditItem(editingItem.itemId, {
          category_id: editingItem.categoryId,
          name: editingItem.name,
          status: editingItem.status,
          priority: editingItem.priority,
          cost: editingItem.cost,
          cost_type: editingItem.cost_type,
          notes: editingItem.notes
        });
      } else {
        // Create new item
        await ctoDashboardApi.createAuditItem({
          category_id: editingItem.categoryId,
          name: editingItem.name,
          status: editingItem.status,
          priority: editingItem.priority,
          cost: editingItem.cost,
          cost_type: editingItem.cost_type,
          notes: editingItem.notes
        });
      }
      setEditingItem(null);
      setShowAddItem(null);
      loadData(); // Reload data
    } catch (error) {
      console.error('Failed to save audit item:', error);
    }
  };

  const handleDeleteAuditItem = async (itemId: number) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await ctoDashboardApi.deleteAuditItem(itemId);
      loadData(); // Reload data
    } catch (error) {
      console.error('Failed to delete audit item:', error);
    }
  };

  const handleUpdateSystemStatus = async (id: number, status: ApiSystemStatus['status']) => {
    try {
      await ctoDashboardApi.updateSystemStatus(id, status);
      loadData(); // Reload data
    } catch (error) {
      console.error('Failed to update system status:', error);
    }
  };

  const handleToggleAutomation = async (id: number) => {
    try {
      await ctoDashboardApi.toggleAutomation(id);
      loadData(); // Reload data
    } catch (error) {
      console.error('Failed to toggle automation:', error);
    }
  };

  const overviewMetrics: MetricCard[] = [
    { title: 'Occupancy Rate', value: '87%', change: 5, icon: Home, status: 'good' },
    { title: 'RevPAR', value: '$245', change: 12, icon: DollarSign, status: 'good' },
    { title: 'Guest Satisfaction', value: '4.8/5', change: 2, icon: Star, status: 'good' },
    { title: 'Avg Response Time', value: '12 min', change: -15, icon: Clock, status: 'good' },
    { title: 'Direct Bookings', value: '42%', change: 8, icon: TrendingUp, status: 'good' },
    { title: 'System Uptime', value: '99.8%', change: 0, icon: Activity, status: 'good' },
  ];

  const renderMetricCard = (metric: MetricCard) => {
    const Icon = metric.icon;
    const statusColors = {
      good: 'text-green-500 bg-green-50',
      warning: 'text-yellow-500 bg-yellow-50',
      critical: 'text-red-500 bg-red-50',
    };

    return (
      <div key={metric.title} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className={`p-3 rounded-lg ${metric.status ? statusColors[metric.status] : 'bg-blue-50 text-blue-500'}`}>
            <Icon size={24} />
          </div>
          {metric.change !== undefined && (
            <span className={`text-sm font-medium ${metric.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {metric.change >= 0 ? '+' : ''}{metric.change}%
            </span>
          )}
        </div>
        <div className="mt-4">
          <p className="text-gray-600 text-sm">{metric.title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
        </div>
      </div>
    );
  };

  const renderTechAudit = () => {
    // Calculate summary stats
    const stats = auditCategories.reduce((acc, cat) => {
      cat.items?.forEach(item => {
        if (item.status === 'exists') acc.working++;
        else if (item.status === 'outdated') acc.outdated++;
        else if (item.status === 'missing') acc.missing++;
        
        // Calculate monthly cost
        if (item.cost && item.cost_type === 'monthly') {
          acc.monthlyCost += parseFloat(item.cost) || 0;
        }
      });
      return acc;
    }, { working: 0, outdated: 0, missing: 0, monthlyCost: 0 });

    return (
      <div className="space-y-6">
        {/* Audit Summary */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clipboard className="text-blue-500" />
            Resort Tech Audit Summary
          </h2>
          {loading.audit ? (
            <p className="text-gray-500">Loading audit data...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{stats.working}</p>
                <p className="text-sm text-gray-600">Systems Working</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-3xl font-bold text-yellow-600">{stats.outdated}</p>
                <p className="text-sm text-gray-600">Need Updates</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-3xl font-bold text-red-600">{stats.missing}</p>
                <p className="text-sm text-gray-600">Missing Systems</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">${stats.monthlyCost.toFixed(0)}</p>
                <p className="text-sm text-gray-600">Monthly Investment</p>
              </div>
            </div>
          )}
        </div>

        {/* Detailed Audit Items */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {loading.audit ? (
            <p className="text-gray-500">Loading audit items...</p>
          ) : (
            auditCategories.map((category) => (
              <div key={category.id} className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">{category.name}</h3>
                  <button
                    onClick={() => setShowAddItem(category.id)}
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Add item"
                  >
                    <Plus size={18} className="text-gray-600" />
                  </button>
                </div>
                
                {/* Add new item form */}
                {showAddItem === category.id && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium mb-3 text-blue-900">
                      {editingItem?.itemId ? 'Edit Tech Item' : 'Add New Tech Item'}
                    </h4>
                    
                    {/* Item Name */}
                    <input
                      type="text"
                      placeholder="Item name (e.g., 'Fiber Connection', 'Direct Booking Engine')"
                      className="w-full px-3 py-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editingItem?.name || ''}
                      onChange={(e) => setEditingItem({
                        categoryId: category.id,
                        name: e.target.value,
                        status: editingItem?.status || 'missing',
                        priority: editingItem?.priority || 'medium',
                        cost: editingItem?.cost,
                        cost_type: editingItem?.cost_type || 'monthly',
                        notes: editingItem?.notes
                      })}
                    />
                    
                    {/* Status and Priority */}
                    <div className="flex gap-2 mb-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                        <select
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={editingItem?.status || 'missing'}
                          onChange={(e) => setEditingItem(prev => prev ? {...prev, status: e.target.value as any} : null)}
                        >
                          <option value="exists">✓ Already Exists</option>
                          <option value="missing">✗ Missing/Needed</option>
                          <option value="outdated">⚠ Outdated/Needs Update</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                        <select
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={editingItem?.priority || 'medium'}
                          onChange={(e) => setEditingItem(prev => prev ? {...prev, priority: e.target.value as any} : null)}
                        >
                          <option value="high">🔴 High Priority</option>
                          <option value="medium">🟡 Medium Priority</option>
                          <option value="low">🟢 Low Priority</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Cost and Cost Type */}
                    <div className="flex gap-2 mb-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Cost/Price</label>
                        <input
                          type="text"
                          placeholder="150 or 2.9 (number only)"
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={editingItem?.cost || ''}
                          onChange={(e) => setEditingItem(prev => prev ? {...prev, cost: e.target.value} : null)}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Cost Type</label>
                        <select
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={editingItem?.cost_type || 'monthly'}
                          onChange={(e) => setEditingItem(prev => prev ? {...prev, cost_type: e.target.value as any} : null)}
                        >
                          <option value="monthly">$ Monthly</option>
                          <option value="one-time">$ One-time</option>
                          <option value="percentage">% Percentage</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Notes */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Notes / Details</label>
                      <textarea
                        placeholder="e.g., 'Critical for redundancy', 'Save on OTA commissions', 'Currently using WhatsApp'"
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                        value={editingItem?.notes || ''}
                        onChange={(e) => setEditingItem(prev => prev ? {...prev, notes: e.target.value} : null)}
                      />
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveAuditItem}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium"
                        disabled={!editingItem?.name}
                      >
                        <Save size={16} className="inline mr-1" />
                        Save Item
                      </button>
                      <button
                        onClick={() => {
                          setShowAddItem(null);
                          setEditingItem(null);
                        }}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="space-y-3">
                  {category.items?.map((item) => (
                    <div key={item.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {item.status === 'exists' && <CheckCircle className="text-green-500" size={18} />}
                          {item.status === 'missing' && <X className="text-red-500" size={18} />}
                          {item.status === 'outdated' && <AlertCircle className="text-yellow-500" size={18} />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{item.name}</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              item.priority === 'high' ? 'bg-red-100 text-red-700' :
                              item.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {item.priority}
                            </span>
                          </div>
                          {item.notes && <p className="text-sm text-gray-600 mt-1">{item.notes}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.cost && (
                          <span className="text-sm font-medium text-gray-700">
                            {item.cost_type === 'percentage' ? `${item.cost}%` : `$${item.cost}`}
                            {item.cost_type === 'monthly' && '/mo'}
                          </span>
                        )}
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <button
                            onClick={() => {
                              setEditingItem({
                                categoryId: category.id,
                                itemId: item.id,
                                name: item.name,
                                status: item.status,
                                priority: item.priority,
                                cost: item.cost,
                                cost_type: item.cost_type,
                                notes: item.notes
                              });
                              setShowAddItem(category.id);
                            }}
                            className="p-1 hover:bg-blue-100 rounded"
                            title="Edit"
                          >
                            <Edit2 size={14} className="text-blue-600" />
                          </button>
                          <button
                            onClick={() => item.id && handleDeleteAuditItem(item.id)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Delete"
                          >
                            <Trash2 size={14} className="text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

      {/* Stakeholder Interviews Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="text-purple-500" />
          Stakeholder Interview Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { role: 'Owners', insight: 'Need 20% revenue growth, reduce OTA fees', priority: 'Direct bookings' },
            { role: 'Front Desk', insight: 'Too many manual processes, guests wait too long', priority: 'Digital check-in' },
            { role: 'Housekeeping', insight: 'No real-time room status, miscommunication', priority: 'Status dashboard' },
            { role: 'Guests', insight: 'Slow Wi-Fi, hard to find activities info', priority: 'Better connectivity' },
          ].map((stakeholder, idx) => (
            <div key={idx} className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-lg border">
              <h4 className="font-semibold text-purple-700 mb-2">{stakeholder.role}</h4>
              <p className="text-sm text-gray-600 mb-2">{stakeholder.insight}</p>
              <p className="text-xs font-medium text-blue-600">Priority: {stakeholder.priority}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
    );
  };

  const renderRoadmap = () => (
    <div className="space-y-6">
      {/* Phase Timeline */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
          <Map className="text-blue-500" />
          Phased Implementation Roadmap
        </h2>
        
        {loading.phases ? (
          <p className="text-gray-500">Loading phases...</p>
        ) : (
          <div className="space-y-4">
            {phases.map((phase, idx) => (
            <div key={phase.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                    phase.status === 'completed' ? 'bg-green-500' :
                    phase.status === 'in-progress' ? 'bg-blue-500' :
                    'bg-gray-300'
                  }`}>
                    {phase.phase_number}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{phase.name}</h3>
                    <p className="text-gray-600">{phase.goal}</p>
                    <p className="text-sm text-gray-500 mt-1">Focus: {phase.tech_focus}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    phase.status === 'completed' ? 'bg-green-100 text-green-700' :
                    phase.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {phase.status === 'completed' ? 'Completed' :
                     phase.status === 'in-progress' ? 'In Progress' : 'Not Started'}
                  </span>
                  <p className="text-sm text-gray-500 mt-2">{phase.timeframe}</p>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-3">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>{phase.completed_tasks}/{phase.total_tasks} tasks</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      phase.status === 'completed' ? 'bg-green-500' :
                      phase.status === 'in-progress' ? 'bg-blue-500' :
                      'bg-gray-300'
                    }`}
                    style={{ width: `${(phase.completed_tasks / phase.total_tasks) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {phase.status === 'in-progress' && (
                <div className="mt-3 p-3 bg-blue-50 rounded">
                  <p className="text-sm font-medium text-blue-700">Current Tasks:</p>
                  <ul className="mt-1 space-y-1">
                    <li className="text-sm text-gray-700">• Complete payment gateway integration</li>
                    <li className="text-sm text-gray-700">• Set up direct booking engine</li>
                    <li className="text-sm text-gray-700">• Configure OTA channel sync</li>
                  </ul>
                </div>
              )}
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Integration Ecosystem Map */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Network className="text-green-500" />
          System Integration Map
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-700 mb-3">Guest-Facing</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Website</span>
                <ArrowRight className="text-gray-400" size={16} />
                <span>Booking Engine</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Mobile App</span>
                <ArrowRight className="text-gray-400" size={16} />
                <span>Check-in</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>QR Codes</span>
                <ArrowRight className="text-gray-400" size={16} />
                <span>AI Concierge</span>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-semibold text-green-700 mb-3">Operations</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>PMS</span>
                <ArrowRight className="text-gray-400" size={16} />
                <span>Housekeeping</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>POS</span>
                <ArrowRight className="text-gray-400" size={16} />
                <span>Inventory</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Staff App</span>
                <ArrowRight className="text-gray-400" size={16} />
                <span>Scheduling</span>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg">
            <h4 className="font-semibold text-purple-700 mb-3">Data & Automation</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Zapier/n8n</span>
                <ArrowRight className="text-gray-400" size={16} />
                <span>Workflows</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Analytics</span>
                <ArrowRight className="text-gray-400" size={16} />
                <span>Dashboards</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>AI Models</span>
                <ArrowRight className="text-gray-400" size={16} />
                <span>Predictions</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAIConcierge = () => (
    <div className="space-y-6">
      {/* AI Concierge Overview */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-sm p-6 text-white">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
          <Bot className="text-white" size={28} />
          AI-Powered Concierge System
        </h2>
        <p className="text-purple-100 mb-6">
          Your signature differentiator: 24/7 multilingual guest support with instant responses
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white/20 backdrop-blur rounded-lg p-4">
            <p className="text-3xl font-bold">2,847</p>
            <p className="text-sm text-purple-100">Total Conversations</p>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-lg p-4">
            <p className="text-3xl font-bold">8 sec</p>
            <p className="text-sm text-purple-100">Avg Response Time</p>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-lg p-4">
            <p className="text-3xl font-bold">94%</p>
            <p className="text-sm text-purple-100">Resolution Rate</p>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-lg p-4">
            <p className="text-3xl font-bold">4.9/5</p>
            <p className="text-sm text-purple-100">Guest Rating</p>
          </div>
        </div>
      </div>

      {/* AI Features */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MessageSquare className="text-blue-500" />
            Most Common Queries
          </h3>
          <div className="space-y-3">
            {[
              { query: "What's the Wi-Fi password?", count: 432, automated: true },
              { query: "Book a boat tour", count: 218, automated: true },
              { query: "Room service menu", count: 187, automated: true },
              { query: "Late checkout request", count: 156, automated: true },
              { query: "Local restaurant recommendations", count: 142, automated: true },
              { query: "Airport transfer", count: 98, automated: false },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{item.query}</span>
                  {item.automated && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Automated
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium text-gray-600">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Lightbulb className="text-yellow-500" />
            AI Capabilities
          </h3>
          <div className="space-y-3">
            {[
              { feature: 'Multilingual Support', status: 'active', languages: 'EN, PT, ES, FR' },
              { feature: 'Booking Integration', status: 'active', description: 'Direct room bookings' },
              { feature: 'Service Requests', status: 'active', description: 'Housekeeping, maintenance' },
              { feature: 'Local Recommendations', status: 'active', description: 'Restaurants, activities' },
              { feature: 'Payment Processing', status: 'development', description: 'In-chat payments' },
              { feature: 'Voice Assistant', status: 'planned', description: 'Alexa/Google integration' },
            ].map((capability, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{capability.feature}</p>
                  <p className="text-xs text-gray-600">
                    {capability.languages || capability.description}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  capability.status === 'active' ? 'bg-green-100 text-green-700' :
                  capability.status === 'development' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {capability.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* QR Code Touchpoints */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Smartphone className="text-purple-500" />
          QR Code Touchpoints
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { location: 'Room Nightstand', scans: 342, purpose: 'General concierge' },
            { location: 'Pool Area', scans: 218, purpose: 'Towel/drink service' },
            { location: 'Restaurant', scans: 187, purpose: 'Menu & ordering' },
            { location: 'Beach Chairs', scans: 156, purpose: 'Activity booking' },
            { location: 'Reception', scans: 142, purpose: 'Check-in/out' },
            { location: 'Parking', scans: 98, purpose: 'Valet service' },
            { location: 'Spa', scans: 76, purpose: 'Treatment booking' },
            { location: 'Gym', scans: 45, purpose: 'Class schedule' },
          ].map((point, idx) => (
            <div key={idx} className="p-3 border rounded-lg hover:shadow-md transition-shadow">
              <p className="font-medium text-sm">{point.location}</p>
              <p className="text-2xl font-bold text-purple-600 my-1">{point.scans}</p>
              <p className="text-xs text-gray-600">{point.purpose}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAutomation = () => (
    <div className="space-y-6">
      {/* Automation Overview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Zap className="text-yellow-500" />
          Automation Workflows
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-3xl font-bold text-green-600">42</p>
            <p className="text-sm text-gray-600">Active Automations</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-3xl font-bold text-blue-600">8,432</p>
            <p className="text-sm text-gray-600">Tasks Automated/Month</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-3xl font-bold text-purple-600">126 hrs</p>
            <p className="text-sm text-gray-600">Time Saved/Month</p>
          </div>
        </div>
      </div>

      {/* Active Workflows */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Guest Journey Automations</h3>
          <div className="space-y-3">
            {[
              { 
                name: 'Booking Confirmation',
                trigger: 'New reservation',
                actions: ['Send WhatsApp', 'Create guest profile', 'Schedule pre-arrival'],
                status: 'active',
                runs: 312
              },
              {
                name: 'Pre-Arrival Sequence',
                trigger: '3 days before',
                actions: ['Send check-in link', 'Weather update', 'Upsell offers'],
                status: 'active',
                runs: 298
              },
              {
                name: 'Check-In Flow',
                trigger: 'Digital check-in',
                actions: ['Generate room code', 'Notify housekeeping', 'Send welcome'],
                status: 'active',
                runs: 276
              },
              {
                name: 'Post-Stay Follow-up',
                trigger: '1 day after checkout',
                actions: ['Request review', 'Send thank you', 'Loyalty offer'],
                status: 'active',
                runs: 265
              }
            ].map((workflow, idx) => (
              <div key={idx} className="p-3 border rounded-lg hover:bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium">{workflow.name}</p>
                    <p className="text-xs text-gray-500">Trigger: {workflow.trigger}</p>
                  </div>
                  <div className="text-right">
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      Active
                    </span>
                    <p className="text-xs text-gray-500 mt-1">{workflow.runs} runs</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {workflow.actions.map((action, actionIdx) => (
                    <span key={actionIdx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {action}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Operations Automations</h3>
          <div className="space-y-3">
            {[
              {
                name: 'Room Status Sync',
                trigger: 'Housekeeping update',
                actions: ['Update PMS', 'Notify front desk', 'Update availability'],
                frequency: 'Real-time',
                impact: 'High'
              },
              {
                name: 'Inventory Reorder',
                trigger: 'Low stock alert',
                actions: ['Create PO', 'Send to supplier', 'Update budget'],
                frequency: 'Weekly',
                impact: 'Medium'
              },
              {
                name: 'Staff Scheduling',
                trigger: 'Occupancy forecast',
                actions: ['Generate schedule', 'Send shifts', 'Update payroll'],
                frequency: 'Weekly',
                impact: 'High'
              },
              {
                name: 'Maintenance Alerts',
                trigger: 'IoT sensor',
                actions: ['Create ticket', 'Assign staff', 'Order parts'],
                frequency: 'As needed',
                impact: 'Critical'
              }
            ].map((automation, idx) => (
              <div key={idx} className="p-3 border rounded-lg hover:bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium">{automation.name}</p>
                    <p className="text-xs text-gray-500">Frequency: {automation.frequency}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    automation.impact === 'Critical' ? 'bg-red-100 text-red-700' :
                    automation.impact === 'High' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {automation.impact} Impact
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {automation.actions.map((action, actionIdx) => (
                    <span key={actionIdx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                      {action}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Automation Builder */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Wrench className="text-gray-600" />
          Quick Automation Builder
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="font-medium mb-2">When this happens...</p>
            <select className="w-full p-2 border rounded">
              <option>Guest books room</option>
              <option>Guest checks in</option>
              <option>Room cleaned</option>
              <option>Payment received</option>
              <option>Review posted</option>
            </select>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="font-medium mb-2">Do this...</p>
            <select className="w-full p-2 border rounded">
              <option>Send WhatsApp message</option>
              <option>Update database</option>
              <option>Create task</option>
              <option>Send email</option>
              <option>Trigger webhook</option>
            </select>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="font-medium mb-2">Then...</p>
            <select className="w-full p-2 border rounded">
              <option>Log activity</option>
              <option>Update dashboard</option>
              <option>Notify team</option>
              <option>Schedule follow-up</option>
              <option>Generate report</option>
            </select>
          </div>
        </div>
        <button className="mt-4 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors">
          Create Automation
        </button>
      </div>
    </div>
  );

  const renderGuestExperience = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MessageSquare className="text-blue-500" size={20} />
            Guest Communications
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active Conversations</span>
              <span className="font-semibold">24</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Avg Response Time</span>
              <span className="font-semibold text-green-600">8 min</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Satisfaction Score</span>
              <span className="font-semibold">4.9/5</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Smartphone className="text-purple-500" size={20} />
            Digital Check-ins Today
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Mobile Check-ins</span>
              <span className="font-semibold">18/25</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Kiosk Check-ins</span>
              <span className="font-semibold">4/25</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Traditional</span>
              <span className="font-semibold">3/25</span>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-500 h-2 rounded-full" style={{ width: '88%' }}></div>
              </div>
              <p className="text-sm text-gray-600 mt-1">88% Digital Adoption</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Key className="text-green-500" size={20} />
            Smart Lock Status
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active Locks</span>
              <span className="font-semibold text-green-600">52/52</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Low Battery</span>
              <span className="font-semibold text-yellow-600">3</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Access Granted Today</span>
              <span className="font-semibold">147</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Current Guest Requests</h3>
        <div className="space-y-3">
          {[
            { room: '204', request: 'Extra towels', time: '5 min ago', priority: 'low' },
            { room: '312', request: 'Room service - Dinner', time: '12 min ago', priority: 'medium' },
            { room: '108', request: 'AC not working', time: '18 min ago', priority: 'high' },
            { room: '220', request: 'Late checkout request', time: '25 min ago', priority: 'medium' },
          ].map((req, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${
                  req.priority === 'high' ? 'bg-red-500' : 
                  req.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                }`}></span>
                <div>
                  <p className="font-medium">Room {req.room}</p>
                  <p className="text-sm text-gray-600">{req.request}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">{req.time}</p>
                <button className="text-blue-600 text-sm hover:underline">Assign</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderOperations = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Housekeeping Status</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-green-600">18</p>
                <p className="text-sm text-gray-600">Clean</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-yellow-600">12</p>
                <p className="text-sm text-gray-600">In Progress</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-red-600">8</p>
                <p className="text-sm text-gray-600">Dirty</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Staff on Duty</span>
                <span className="font-medium">6/8</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Avg Time per Room</span>
                <span className="font-medium">28 min</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Est. Completion</span>
                <span className="font-medium">2:30 PM</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Maintenance Tasks</h3>
          <div className="space-y-3">
            {[
              { task: 'AC Unit - Room 204', priority: 'high', assigned: 'John D.', status: 'in-progress' },
              { task: 'Pool Filter Check', priority: 'medium', assigned: 'Mike R.', status: 'pending' },
              { task: 'Elevator Inspection', priority: 'low', assigned: 'External', status: 'scheduled' },
              { task: 'Parking Lot Lights', priority: 'medium', assigned: 'John D.', status: 'pending' },
            ].map((task, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    task.priority === 'high' ? 'bg-red-500' : 
                    task.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}></span>
                  <div>
                    <p className="text-sm font-medium">{task.task}</p>
                    <p className="text-xs text-gray-500">Assigned to: {task.assigned}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  task.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                  task.status === 'pending' ? 'bg-gray-100 text-gray-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {task.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Staff Schedule - Today</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Department</th>
                <th className="text-center py-2">6-2</th>
                <th className="text-center py-2">2-10</th>
                <th className="text-center py-2">10-6</th>
                <th className="text-center py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2">Front Desk</td>
                <td className="text-center">2</td>
                <td className="text-center">3</td>
                <td className="text-center">1</td>
                <td className="text-center font-medium">6</td>
              </tr>
              <tr className="border-b">
                <td className="py-2">Housekeeping</td>
                <td className="text-center">4</td>
                <td className="text-center">4</td>
                <td className="text-center">-</td>
                <td className="text-center font-medium">8</td>
              </tr>
              <tr className="border-b">
                <td className="py-2">Maintenance</td>
                <td className="text-center">1</td>
                <td className="text-center">2</td>
                <td className="text-center">1</td>
                <td className="text-center font-medium">4</td>
              </tr>
              <tr className="border-b">
                <td className="py-2">Security</td>
                <td className="text-center">1</td>
                <td className="text-center">1</td>
                <td className="text-center">2</td>
                <td className="text-center font-medium">4</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderRevenue = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {[
          { label: "Today's Revenue", value: '$12,450', change: '+15%', color: 'green' },
          { label: 'ADR', value: '$285', change: '+8%', color: 'blue' },
          { label: 'Direct Bookings', value: '42%', change: '+5%', color: 'purple' },
          { label: 'Ancillary Revenue', value: '$3,200', change: '+22%', color: 'orange' },
        ].map((stat, idx) => (
          <div key={idx} className="bg-white rounded-xl shadow-sm p-6">
            <p className="text-gray-600 text-sm mb-1">{stat.label}</p>
            <p className="text-2xl font-bold">{stat.value}</p>
            <p className={`text-sm mt-1 text-${stat.color}-600`}>{stat.change} vs last week</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Channel Performance</h3>
        <div className="space-y-4">
          {[
            { channel: 'Direct Website', bookings: 42, revenue: '$5,200', commission: '0%' },
            { channel: 'Booking.com', bookings: 28, revenue: '$3,360', commission: '15%' },
            { channel: 'Expedia', bookings: 18, revenue: '$2,160', commission: '18%' },
            { channel: 'Airbnb', bookings: 12, revenue: '$1,730', commission: '12%' },
          ].map((channel, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <p className="font-medium">{channel.channel}</p>
                <p className="text-sm text-gray-600">{channel.bookings} bookings</p>
              </div>
              <div className="text-right">
                <p className="font-semibold">{channel.revenue}</p>
                <p className="text-sm text-gray-600">Commission: {channel.commission}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Pricing Optimization</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Base Rate</span>
              <span className="font-medium">$250</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Current Rate</span>
              <span className="font-medium text-green-600">$285</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Competitor Avg</span>
              <span className="font-medium">$272</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Recommended</span>
              <span className="font-medium text-blue-600">$295</span>
            </div>
            <button className="w-full mt-4 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Apply Recommended Rate
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Upsell Performance</h3>
          <div className="space-y-3">
            {[
              { item: 'Late Checkout', sold: 8, revenue: '$320' },
              { item: 'Room Upgrade', sold: 5, revenue: '$450' },
              { item: 'Breakfast Package', sold: 12, revenue: '$360' },
              { item: 'Spa Services', sold: 6, revenue: '$780' },
            ].map((item, idx) => (
              <div key={idx} className="flex justify-between items-center">
                <span className="text-gray-600">{item.item}</span>
                <div className="text-right">
                  <span className="font-medium">{item.revenue}</span>
                  <span className="text-sm text-gray-500 ml-2">({item.sold} sold)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderInfrastructure = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">System Health</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {systemStatuses.map((system, idx) => (
            <div key={idx} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{system.name}</span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  system.status === 'online' ? 'bg-green-100 text-green-700' :
                  system.status === 'degraded' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {system.status}
                </span>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Uptime</span>
                  <span className="font-medium">99.9%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Check</span>
                  <span className="text-gray-500">Just now</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Wifi className="text-blue-500" size={20} />
            Network Status
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Guest WiFi Users</span>
              <span className="font-medium">142</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Bandwidth Usage</span>
              <span className="font-medium">68%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Avg Speed</span>
              <span className="font-medium text-green-600">85 Mbps</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Failed Connections</span>
              <span className="font-medium text-yellow-600">3</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Database className="text-purple-500" size={20} />
            Data & Backups
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Last Backup</span>
              <span className="font-medium text-green-600">2 hours ago</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Storage Used</span>
              <span className="font-medium">42.3 GB</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Database Size</span>
              <span className="font-medium">8.7 GB</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Next Backup</span>
              <span className="font-medium">10:00 PM</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Security Events</h3>
        <div className="space-y-2">
          {[
            { event: 'Failed login attempt - Admin Panel', time: '15 min ago', severity: 'medium' },
            { event: 'SSL Certificate renewed', time: '2 hours ago', severity: 'info' },
            { event: 'Firewall blocked suspicious IP', time: '4 hours ago', severity: 'high' },
            { event: 'System update completed', time: '6 hours ago', severity: 'info' },
          ].map((event, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${
                  event.severity === 'high' ? 'bg-red-500' :
                  event.severity === 'medium' ? 'bg-yellow-500' :
                  'bg-blue-500'
                }`}></span>
                <span className="text-sm">{event.event}</span>
              </div>
              <span className="text-xs text-gray-500">{event.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">CTO Dashboard</h1>
              <span className="text-sm text-gray-500">Beachside Resource</span>
            </div>
            <div className="flex items-center gap-4">
              <select 
                className="px-4 py-2 border rounded-lg text-sm"
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>
              <div className="relative">
                <Bell className="text-gray-600 cursor-pointer" size={20} />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </div>
              <Settings className="text-gray-600 cursor-pointer" size={20} />
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="px-6">
          <nav className="flex gap-6 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: Monitor },
              { id: 'audit', label: 'Tech Audit', icon: Clipboard },
              { id: 'roadmap', label: 'Roadmap', icon: Map },
              { id: 'guest', label: 'Guest Experience', icon: Users },
              { id: 'operations', label: 'Operations', icon: Wrench },
              { id: 'revenue', label: 'Revenue', icon: DollarSign },
              { id: 'infrastructure', label: 'Infrastructure', icon: Cloud },
              { id: 'ai-concierge', label: 'AI Concierge', icon: Bot },
              { id: 'automation', label: 'Automation', icon: Zap },
            ].map((tab) => {
              const Icon = tab.icon;
              const path = tab.id === 'overview' ? '/cto' : `/cto/${tab.id}`;
              return (
                <Link
                  key={tab.id}
                  to={path}
                  className={`flex items-center gap-2 py-4 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon size={18} />
                  <span className="font-medium">{tab.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="px-6 py-6">
        <Routes>
          <Route path="/" element={
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                {overviewMetrics.map(renderMetricCard)}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'View Arrivals', icon: Calendar, count: 25 },
                    { label: 'Pending Requests', icon: Bell, count: 8 },
                    { label: 'Staff Schedule', icon: Users, count: 22 },
                    { label: 'System Alerts', icon: AlertCircle, count: 3 },
                  ].map((action, idx) => (
                    <button key={idx} className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <action.icon className="text-gray-600 mb-2" size={24} />
                      <span className="text-sm font-medium">{action.label}</span>
                      {action.count > 0 && (
                        <span className="mt-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {action.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div key={notif.id} className="flex items-start gap-3">
                      {notif.type === 'success' && <CheckCircle className="text-green-500 mt-0.5" size={18} />}
                      {notif.type === 'warning' && <AlertCircle className="text-yellow-500 mt-0.5" size={18} />}
                      {notif.type === 'info' && <Activity className="text-blue-500 mt-0.5" size={18} />}
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{notif.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{notif.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          } />
          <Route path="/audit" element={renderTechAudit()} />
          <Route path="/roadmap" element={renderRoadmap()} />
          <Route path="/guest" element={renderGuestExperience()} />
          <Route path="/operations" element={renderOperations()} />
          <Route path="/revenue" element={renderRevenue()} />
          <Route path="/infrastructure" element={renderInfrastructure()} />
          <Route path="/ai-concierge" element={renderAIConcierge()} />
          <Route path="/automation" element={renderAutomation()} />
        </Routes>
      </main>
    </div>
  );
};

export default CTODashboard;